import { Entity } from '@backstage/catalog-model';
import {
  CatalogAutocomplete,
  DefaultEntityFilters,
  EntityFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { ACCOUNT_ENABLED_ANNOTATION } from './constant.tsx';

// ── Filter class ─────────────────────────────────────────────────────────────

export type UserAccountStatusValue = 'Active' | 'Inactive';

export class UserAccountStatusFilter implements EntityFilter {
  constructor(readonly values: UserAccountStatusValue[]) {}

  filterEntity(entity: Entity): boolean {
    if (!this.values.length) return true;
    const isEnabled =
      entity.metadata.annotations?.[ACCOUNT_ENABLED_ANNOTATION] !== 'false';
    const label: UserAccountStatusValue = isEnabled ? 'Active' : 'Inactive';
    return this.values.includes(label);
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

// ── Extended filter bag ───────────────────────────────────────────────────────

export interface CustomEntityFilters extends DefaultEntityFilters {
  userAccountStatus?: UserAccountStatusFilter;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles(
  { input: {} },
  { name: 'UserAccountStatusPicker' },
);

// ── Picker component ──────────────────────────────────────────────────────────

const ALL_OPTIONS: UserAccountStatusValue[] = ['Active', 'Inactive'];

export function UserAccountStatusPicker() {
  const classes = useStyles();
  const { filters, updateFilters } = useEntityList<CustomEntityFilters>();

  const [selected, setSelected] = useState<UserAccountStatusValue[]>(
    filters.userAccountStatus?.values ?? [],
  );

  // Only render when the "user" kind is selected
  const kind = filters.kind?.value?.toLocaleLowerCase('en-US') ?? '';

  useEffect(() => {
    if (kind !== 'user') return;
    updateFilters({
      userAccountStatus: selected.length
        ? new UserAccountStatusFilter(selected)
        : undefined,
    });
  }, [selected, updateFilters, kind]);

  if (kind !== 'user') return null;

  return (
    <Box pb={1} pt={1}>
      <CatalogAutocomplete<UserAccountStatusValue, true, false, false>
        multiple
        disableCloseOnSelect
        label="Status"
        name="user-account-status-picker"
        options={ALL_OPTIONS}
        value={selected}
        TextFieldProps={{ className: classes.input }}
        onChange={(_event, values) =>
          setSelected(values as UserAccountStatusValue[])
        }
        renderOption={(option, { selected: isSelected }) => (
          <>
            <Checkbox style={{ marginRight: 4 }} checked={isSelected} size="small" />
            <ListItemText primary={option} />
          </>
        )}
      />
    </Box>
  );
}
